import axios, { AxiosRequestConfig, CanceledError } from "axios";
import { CLIENT_ID, CLIENT_ID_HEADER } from "./clientId";

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

axios.defaults.withCredentials = true;

// ─── Auth Store Registry ───
// AuthStore registers itself here so non-React modules (this interceptor and
// createApiConfig) can read the current in-memory token and trigger a refresh.
type AuthStoreRef = {
  refreshToken: () => Promise<boolean>;
  setAuthToken: (token: string | null) => void;
  authToken: string | null;
};

let authStoreInstance: AuthStoreRef | null = null;

export const registerAuthStore = (store: AuthStoreRef) => {
  authStoreInstance = store;
  console.info("[AXIOS] AuthStore registered for token refresh");
};

export const getRegisteredAuthStore = (): AuthStoreRef | null => authStoreInstance;

// ─── Diagnostic: log every outgoing request's Authorization header ───
axios.interceptors.request.use((config) => {
  // Tag every request with this tab's client id so the backend can attribute
  // websocket broadcasts to their initiator (frontend echo suppression).
  if (config.headers) {
    config.headers[CLIENT_ID_HEADER] = CLIENT_ID;
  }

  const authHeader = config.headers?.Authorization || config.headers?.authorization;
  const url = `${config.method?.toUpperCase()} ${config.baseURL || ""}${config.url || ""}`;

  if (!authHeader) {
    console.warn(`[AUTH-DIAG] ❌ NO Authorization header on request: ${url}`);
  } else if (
    authHeader === "Bearer " ||
    authHeader === "Bearer null" ||
    authHeader === "Bearer undefined"
  ) {
    // Only endpoints that require bearer auth carry this header, so an empty
    // token means we are logged out. Cancel locally instead of provoking a 401.
    console.warn(`[AUTH-DIAG] ⚠️ Cancelling request without auth token: ${url}`);
    throw new CanceledError(`Request cancelled: no auth token (${url})`, undefined, config);
  }
  return config;
});

// ─── 401 interceptor: attempt token refresh, then retry once ───
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (value?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  async (response) => {
    if (process.env.REACT_APP_API_DELAY) {
      await delay(Number(process.env.REACT_APP_API_DELAY));
    }
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const url = `${originalRequest?.method?.toUpperCase()} ${originalRequest?.baseURL || ""}${originalRequest?.url || ""}`;

    console.info(`[AXIOS-INTERCEPTOR] Response error: status=${status}, url=${url}`);

    if (error.response?.status === 401) {
      const url = `${originalRequest?.method?.toUpperCase()} ${originalRequest?.baseURL || ""}${originalRequest?.url || ""}`;
      const sentHeader = originalRequest?.headers?.Authorization || "(none)";
      const localToken = authStoreInstance?.authToken ?? null;

      console.error(
        `[AUTH-DIAG] 🔴 401 Unauthorized\n` +
          `  URL: ${url}\n` +
          `  Sent header: ${String(sentHeader).slice(0, 20)}…\n` +
          `  in-memory token exists: ${!!localToken}\n` +
          `  in-memory token last 8: ${localToken ? "…" + localToken.slice(-8) : "N/A"}`,
      );

      // Skip refresh logic for auth endpoints to prevent infinite loops
      const requestUrl = originalRequest?.url || "";
      console.info(
        `[AUTH-REFRESH] Checking if should refresh. URL: ${requestUrl}, _retry: ${originalRequest._retry}`,
      );

      if (requestUrl.includes("/auth/refresh") || requestUrl.includes("/auth/logout")) {
        console.info(`[AUTH-REFRESH] Skipping refresh for auth endpoint`);
        return Promise.reject(error);
      }

      // If there is no in-memory access token, do not attempt refresh.
      // This prevents refresh storms for anonymous requests after logout.
      if (!authStoreInstance?.authToken) {
        console.info("[AUTH-REFRESH] Skipping refresh: no in-memory token present");
        return Promise.reject(error);
      }

      // ── Attempt token refresh once ──
      if (!originalRequest._retry) {
        console.info(`[AUTH-REFRESH] Will attempt refresh`);
        originalRequest._retry = true;

        if (isRefreshing) {
          // Queue request if refresh is already in progress
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              if (originalRequest.headers && token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return axios(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        isRefreshing = true;

        try {
          console.info(`[AUTH-REFRESH] Attempting token refresh...`);

          let success = false;

          if (authStoreInstance) {
            // Use AuthStore's refreshToken method (sets the in-memory token)
            success = await authStoreInstance.refreshToken();
          } else {
            // AuthStore should always be registered before any API call
            console.error("[AUTH-REFRESH] AuthStore not registered, cannot refresh token");
            success = false;
          }

          if (success) {
            const newToken = authStoreInstance?.authToken ?? null;
            console.info(`[AUTH-REFRESH] Token refreshed successfully`);

            // Update Authorization header with new token
            if (originalRequest.headers && newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            processQueue(null, newToken);
            return axios(originalRequest);
          } else {
            throw new Error("Token refresh failed");
          }
        } catch (refreshError: any) {
          console.error(
            `[AUTH-REFRESH] Token refresh failed:`,
            refreshError?.response?.status,
            refreshError?.message,
          );
          // Ensure we clear stale in-memory auth state on refresh failure.
          authStoreInstance?.setAuthToken?.(null);
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    return Promise.reject(error);
  },
);
